import type { PlanLimit, PlanLimitReachedData, PlanTier } from '@fluenceur/types';
import {
  upgradePaywallFor,
  type PaywallPlan,
  type PaywallScope,
  type PaywallType,
  type UpgradeFeature,
} from '@/libs/paywall-url-state';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export type { UpgradeFeature } from '@/libs/paywall-url-state';

export interface PaywallContext {
  paywall: PaywallType;
  scope: PaywallScope;
  origin: string;
  planLimit: PlanLimitReachedData | null;
  onClose?: () => void;
}

interface PaywallRequest {
  id: number;
  paywall: PaywallType;
  plan: PaywallPlan | null;
  planLimit: PlanLimitReachedData | null;
  scope: PaywallScope | null;
}

interface UpgradeModalState {
  /** A non-React caller's one-shot intent, consumed by the URL host. */
  request: PaywallRequest | null;
  /** A scoped refusal or optional close callback, never an open/closed state. */
  context: PaywallContext | null;
}

interface UpgradeModalActions {
  openUpgradeModal: (
    feature?: UpgradeFeature,
    preferredTier?: PlanTier,
    scope?: PaywallScope | null
  ) => void;
  openPlanLimitModal: (planLimit: PlanLimitReachedData, scope?: PaywallScope | null) => void;
  openInferredPlanLimitModal: (limit: PlanLimit, scope?: PaywallScope | null) => void;
  closeUpgradeModal: () => void;
  setPaywallContext: (context: PaywallContext) => void;
  consumeRequest: (id: number) => void;
}

const initialState: UpgradeModalState = {
  request: null,
  context: null,
};

/** Non-persisted intentions and refusal data; nuqs owns the modal route. */
let requestId = 0;

export const useUpgradeModalStore = create<UpgradeModalState & UpgradeModalActions>()(
  devtools(
    (set) => ({
      ...initialState,

      openUpgradeModal: (
        feature: UpgradeFeature = 'generic',
        preferredTier?: PlanTier,
        scope = null
      ) => {
        set({
          request: {
            id: ++requestId,
            ...upgradePaywallFor(feature, preferredTier),
            planLimit: null,
            scope,
          },
          context: null,
        });
      },

      openPlanLimitModal: (planLimit: PlanLimitReachedData, scope = null) => {
        set({
          request: { id: ++requestId, paywall: planLimit.limit, plan: null, planLimit, scope },
          context: null,
        });
      },

      openInferredPlanLimitModal: (limit: PlanLimit, scope = null) => {
        set({
          request: { id: ++requestId, paywall: limit, plan: null, planLimit: null, scope },
          context: null,
        });
      },

      closeUpgradeModal: () => {
        set(initialState);
      },
      setPaywallContext: (context) => {
        set({ request: null, context });
      },
      consumeRequest: (id) => {
        set((state) => (state.request?.id === id ? { request: null } : state));
      },
    }),
    { name: 'UpgradeModalStore' }
  )
);
